import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Wallet, DollarSign, Car, Bike, Users, UserCheck,
  Clock, FileText, Loader2, AlertTriangle, CheckCircle2, Download,
  CalendarDays, Trophy
} from 'lucide-react';
import AppFooter from '../components/AppFooter';
import BackButton from '../components/BackButton';
import RefreshButton from '../components/RefreshButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";
const CASM_LOGO = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dmndty59_San_Martin_deTucuman.png";

export default function CashierPage() {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [lastClosed, setLastClosed] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [currentRes, historyRes] = await Promise.all([
        axios.get(`${API}/cashier/current`, { withCredentials: true }),
        axios.get(`${API}/cashier/history`, { withCredentials: true })
      ]);
      setCurrent(currentRes.data);
      setHistory(historyRes.data);
    } catch (err) {
      toast.error('Error al cargar datos de caja');
    } finally {
      setIsLoading(false);
    }
  };

  const closeShift = async () => {
    setIsClosing(true);
    try {
      const { data } = await axios.post(`${API}/cashier/close`, {}, { withCredentials: true });
      setLastClosed(data);
      setShowConfirm(false);
      toast.success(`Caja cerrada! Total: $${data.total_revenue}`);
      fetchData();
    } catch (err) {
      toast.error('Error al cerrar caja');
    } finally {
      setIsClosing(false);
    }
  };

  const downloadPdf = (shiftId) => {
    window.open(`${API}/cashier/export/${shiftId}`, '_blank');
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(amount);
  const formatDate = (d) => {
    if (!d || d === 'inicio' || d === 'Inicio del sistema') return 'Inicio';
    try { return new Date(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return d; }
  };
  const formatDuration = (m) => { const h = Math.floor(m/60); const min = m%60; return h > 0 ? `${h}h ${min}m` : `${min}m`; };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <BackButton to="/dashboard" />
              <div className="border-l border-slate-700 pl-4">
                <h1 className="text-lg font-bold text-white">Caja Diaria</h1>
                <p className="text-sm text-slate-400">Cierre de turno y recaudacion</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <RefreshButton onClick={fetchData} loading={isLoading} />
              <img src={LOGO_URL} alt="ParkingVision AI" className="h-8 w-auto hidden sm:block" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1">
        {isLoading ? (
          <div className="text-center py-16 text-slate-400"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />Cargando...</div>
        ) : (
          <div className="space-y-8">
            {/* Current Shift */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2 text-xl">
                  <Wallet className="w-6 h-6 text-emerald-400" />Turno Actual
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Desde: {current?.shift_start ? formatDate(current.shift_start) : 'Inicio del sistema'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="bg-emerald-900/30 border border-emerald-700/50 rounded-xl p-3 sm:p-4 text-center">
                    <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400 mx-auto mb-1 sm:mb-2" />
                    <p className="text-xl sm:text-3xl font-bold text-emerald-400 truncate" data-testid="shift-revenue">
                      {formatCurrency(current?.total_revenue || 0)}
                    </p>
                    <p className="text-slate-400 text-xs sm:text-sm">Recaudado</p>
                  </div>
                  <div className="bg-blue-900/30 border border-blue-700/50 rounded-xl p-3 sm:p-4 text-center">
                    <Car className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400 mx-auto mb-1 sm:mb-2" />
                    <p className="text-2xl sm:text-3xl font-bold text-blue-400" data-testid="shift-vehicles">
                      {current?.total_vehicles || 0}
                    </p>
                    <p className="text-slate-400 text-xs sm:text-sm">Vehiculos</p>
                  </div>
                  <div className="bg-purple-900/30 border border-purple-700/50 rounded-xl p-3 sm:p-4 text-center">
                    <UserCheck className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400 mx-auto mb-1 sm:mb-2" />
                    <p className="text-2xl sm:text-3xl font-bold text-purple-400">{current?.members_count || 0}</p>
                    <p className="text-slate-400 text-xs sm:text-sm">Socios</p>
                  </div>
                  <div className="bg-amber-900/30 border border-amber-700/50 rounded-xl p-3 sm:p-4 text-center">
                    <Users className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400 mx-auto mb-1 sm:mb-2" />
                    <p className="text-2xl sm:text-3xl font-bold text-amber-400">{current?.visitors_count || 0}</p>
                    <p className="text-slate-400 text-xs sm:text-sm">Visitantes</p>
                  </div>
                </div>

                {/* Service breakdown */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="rounded-xl p-3 sm:p-4 bg-blue-900/20 border border-blue-700/40">
                    <div className="flex items-center gap-2 mb-1">
                      <Car className="w-4 h-4 text-blue-300" />
                      <span className="text-blue-200 text-xs sm:text-sm uppercase tracking-wide">Estacionamiento</span>
                    </div>
                    <p className="text-xl sm:text-2xl font-bold text-blue-200 truncate" data-testid="shift-parking-revenue">
                      {formatCurrency(current?.parking_revenue || 0)}
                    </p>
                    <p className="text-slate-400 text-xs">{current?.parking_operations || 0} operaciones</p>
                  </div>
                  <div className="rounded-xl p-3 sm:p-4 bg-amber-900/20 border border-amber-700/40">
                    <div className="flex items-center gap-2 mb-1">
                      <CalendarDays className="w-4 h-4 text-amber-300" />
                      <span className="text-amber-200 text-xs sm:text-sm uppercase tracking-wide">Eventos</span>
                    </div>
                    <p className="text-xl sm:text-2xl font-bold text-amber-200 truncate" data-testid="shift-event-revenue">
                      {formatCurrency(current?.event_revenue || 0)}
                    </p>
                    <p className="text-slate-400 text-xs">{current?.event_operations || 0} tickets</p>
                  </div>
                </div>

                {current?.events_breakdown?.length > 0 && (
                  <div className="rounded-xl p-3 sm:p-4 bg-slate-700/30 border border-slate-700 mb-4 sm:mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Trophy className="w-4 h-4 text-amber-400" />
                      <span className="text-white text-sm font-semibold">Top eventos del turno</span>
                    </div>
                    <div className="space-y-2">
                      {current.events_breakdown.slice(0, 5).map((ev, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 bg-slate-800/40 rounded-lg p-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i === 0 ? 'bg-amber-500 text-slate-900' : i === 1 ? 'bg-slate-400 text-slate-900' : 'bg-slate-600 text-slate-200'}`}>{i + 1}</span>
                            <span className="text-white text-sm font-medium truncate">{ev.name}</span>
                          </div>
                          <div className="text-right">
                            <p className="text-emerald-400 text-sm font-bold whitespace-nowrap">{formatCurrency(ev.revenue)}</p>
                            <p className="text-slate-400 text-[10px]">{ev.tickets} tickets</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="bg-slate-700/50 rounded-lg p-2 sm:p-3 text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Car className="w-4 h-4 text-blue-400" /><span className="text-blue-400 text-xs sm:text-sm">Autos</span>
                    </div>
                    <p className="text-white text-lg sm:text-xl font-bold">{current?.autos_count || 0}</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-2 sm:p-3 text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Bike className="w-4 h-4 text-amber-400" /><span className="text-amber-400 text-xs sm:text-sm">Motos</span>
                    </div>
                    <p className="text-white text-lg sm:text-xl font-bold">{current?.motos_count || 0}</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-2 sm:p-3 text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Car className="w-4 h-4 text-red-400" /><span className="text-red-400 text-xs sm:text-sm">Adentro</span>
                    </div>
                    <p className="text-white text-lg sm:text-xl font-bold">{current?.vehicles_inside || 0}</p>
                  </div>
                </div>

                <Button onClick={() => setShowConfirm(true)} className="w-full h-14 sm:h-16 bg-red-600 hover:bg-red-700 text-base sm:text-xl font-bold" data-testid="close-shift-btn">
                  <Wallet className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" />CERRAR CAJA
                </Button>
              </CardContent>
            </Card>

            {/* Last closed shift receipt */}
            {lastClosed && (
              <Card className="bg-emerald-900/30 border-emerald-600">
                <CardContent className="p-6 text-center">
                  <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                  <p className="text-xl font-bold text-white mb-2">Caja Cerrada</p>
                  <p className="text-3xl font-bold text-emerald-400 mb-4">{formatCurrency(lastClosed.total_revenue)}</p>
                  <p className="text-slate-400 mb-4">{lastClosed.total_vehicles} vehiculos | {lastClosed.members_count} socios | {lastClosed.visitors_count} visitantes</p>
                  <Button onClick={() => downloadPdf(lastClosed.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="download-receipt-btn">
                    <Download className="w-4 h-4 mr-2" />Descargar Recibo PDF
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Shift History */}
            {history.length > 0 && (
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Clock className="w-5 h-5" />Historial de Cierres
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Mobile cards */}
                  <div className="md:hidden space-y-3">
                    {history.map((shift) => (
                      <div key={shift.id} className="rounded-lg p-3 bg-slate-700/30 border border-slate-700" data-testid={`shift-row-${shift.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0">
                            <p className="text-white text-sm font-medium">{formatDate(shift.closed_at)}</p>
                            <p className="text-slate-500 text-xs truncate">{shift.closed_by}</p>
                          </div>
                          <p className="text-emerald-400 font-bold whitespace-nowrap">{formatCurrency(shift.total_revenue)}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-blue-900/20 rounded p-2">
                            <p className="text-blue-300">Estac.</p>
                            <p className="text-white font-semibold">{formatCurrency(shift.parking_revenue || 0)}</p>
                            <p className="text-slate-400 text-[10px]">{shift.parking_operations || 0} ops</p>
                          </div>
                          <div className="bg-amber-900/20 rounded p-2">
                            <p className="text-amber-300">Eventos</p>
                            <p className="text-white font-semibold">{formatCurrency(shift.event_revenue || 0)}</p>
                            <p className="text-slate-400 text-[10px]">{shift.event_operations || 0} tickets</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-2">
                          <div className="flex items-center gap-2 text-xs">
                            <Badge className="bg-purple-600">{shift.members_count} socios</Badge>
                            <Badge variant="outline" className="text-amber-400 border-amber-400/50">{shift.visitors_count} visit.</Badge>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => downloadPdf(shift.id)} className="text-slate-400 hover:text-white h-8 w-8 p-0">
                            <FileText className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left py-3 px-4 text-slate-400 font-medium">Fecha</th>
                          <th className="text-center py-3 px-4 text-slate-400 font-medium">Vehiculos</th>
                          <th className="text-right py-3 px-4 text-blue-300 font-medium">Estacionamiento</th>
                          <th className="text-right py-3 px-4 text-amber-300 font-medium">Eventos</th>
                          <th className="text-center py-3 px-4 text-slate-400 font-medium">Socios</th>
                          <th className="text-center py-3 px-4 text-slate-400 font-medium">Visit.</th>
                          <th className="text-right py-3 px-4 text-slate-400 font-medium">Total</th>
                          <th className="text-right py-3 px-4 text-slate-400 font-medium">PDF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((shift) => (
                          <tr key={shift.id} className="border-b border-slate-700/50 hover:bg-slate-700/30" data-testid={`shift-row-${shift.id}`}>
                            <td className="py-3 px-4">
                              <p className="text-white text-sm">{formatDate(shift.closed_at)}</p>
                              <p className="text-slate-500 text-xs">{shift.closed_by}</p>
                            </td>
                            <td className="py-3 px-4 text-center text-white font-semibold">{shift.total_vehicles}</td>
                            <td className="py-3 px-4 text-right">
                              <p className="text-blue-300 font-semibold">{formatCurrency(shift.parking_revenue || 0)}</p>
                              <p className="text-slate-500 text-[10px]">{shift.parking_operations || 0} ops</p>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <p className="text-amber-300 font-semibold">{formatCurrency(shift.event_revenue || 0)}</p>
                              <p className="text-slate-500 text-[10px]">{shift.event_operations || 0} tickets</p>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Badge className="bg-purple-600">{shift.members_count}</Badge>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Badge variant="outline" className="text-amber-400 border-amber-400/50">{shift.visitors_count}</Badge>
                            </td>
                            <td className="py-3 px-4 text-right text-emerald-400 font-bold">{formatCurrency(shift.total_revenue)}</td>
                            <td className="py-3 px-4 text-right">
                              <Button variant="ghost" size="sm" onClick={() => downloadPdf(shift.id)} className="text-slate-400 hover:text-white h-8 w-8 p-0" data-testid={`download-shift-${shift.id}`}>
                                <FileText className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>

      <AppFooter />

      {/* Confirm Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-amber-400" />Cerrar Caja
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-slate-300">
              Vas a cerrar el turno actual. Esto genera un corte de caja con el resumen de recaudacion.
            </p>
            {current && (
              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-slate-400 text-sm">Resumen a cerrar:</p>
                <p className="text-2xl font-bold text-emerald-400">{formatCurrency(current.total_revenue)}</p>
                <p className="text-slate-400 text-sm">{current.total_vehicles} vehiculos ({current.members_count} socios, {current.visitors_count} visitantes)</p>
              </div>
            )}
            {current?.vehicles_inside > 0 && (
              <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-3">
                <p className="text-amber-300 text-sm">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Hay {current.vehicles_inside} vehiculo(s) adentro. Seguiran activos en el proximo turno.
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowConfirm(false)} className="flex-1 border-slate-600 text-slate-300">Cancelar</Button>
              <Button onClick={closeShift} disabled={isClosing} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-close-btn">
                {isClosing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wallet className="w-4 h-4 mr-2" />}
                Cerrar Caja
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
